import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sanitizeReportDocument } from "@/lib/sanitize";
import {
  buildReportDoc,
  isFullHtmlDoc,
  isHtmlContent,
  markdownToHtml,
  reportFileName,
  widenReportDoc,
} from "@/lib/reportDoc";
import { NO_WEEK } from "@/lib/reportWeeks";
import WeekPicker from "@/components/WeekPicker";
import { sortByName, sortByTitle } from "@/lib/sortRows";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import PagedList from "@/components/PagedList";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RecipientFields, { allRecipients } from "@/components/RecipientFields";
import ScheduleRecipientFields from "@/components/ScheduleRecipientFields";
import ScheduleDeliveryHistory from "@/components/ScheduleDeliveryHistory";
import { localPreview } from "@/lib/scheduleTime";
import { isEligibleInitiativeOwner } from "@/lib/initiativeOwners";
import {
  ArchiveIcon,
  ArrowsInSimpleIcon,
  ArrowsOutSimpleIcon,
  ArrowUUpLeftIcon,
  CaretDownIcon,
  CheckCircleIcon,
  ClockCounterClockwiseIcon,
  DownloadSimpleIcon,
  PaperPlaneRightIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const SCHEDULE_DAYS = [
  { v: "mon", label: "Monday" }, { v: "tue", label: "Tuesday" }, { v: "wed", label: "Wednesday" },
  { v: "thu", label: "Thursday" }, { v: "fri", label: "Friday" }, { v: "sat", label: "Saturday" }, { v: "sun", label: "Sunday" },
];

// "2026-10-05" -> "Mon, 5 Oct 2026" (a plain calendar date, so no timezone shift).
const fmtReportDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
};

export default function ReportsPage() {
  const { user: currentUser } = useAuth();
  // Admins, managers and owners may generate a report for any project or
  // person (generate-report's own isPrivileged check says the same); only a
  // plain member is limited to what they're part of.
  const canReportOnAnyone = ["admin", "manager", "owner"].includes(currentUser?.role);
  const { search } = useLocation();
  const highlightId = useMemo(() => new URLSearchParams(search).get("highlight"), [search]);
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [scope, setScope] = useState("project");
  const [target, setTarget] = useState("");
  // Which week the report covers; NO_WEEK = the whole project/person as it stands now.
  const [week, setWeek] = useState(NO_WEEK);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // Emailing a report, mirroring the Digest page's send panel.
  const [mail, setMail] = useState({ to: [], cc: [], bcc: [] });
  // Optional note shown above the report body for this one send only — not
  // saved anywhere, not part of the recurring schedule.
  const [sendMessage, setSendMessage] = useState("");
  // History filters. A report's scope_type/scope_id already say what it is
  // about, so these need no new data — just a way to ask.
  // Needed to work out a project's members for the Cc pool.
  const [memberships, setMemberships] = useState([]);
  const [histProject, setHistProject] = useState("all");
  const [histMember, setHistMember] = useState("all");
  const [sending, setSending] = useState(false);
  // Personal archive — defaults to hiding what this user has archived, same
  // "Active" default as TeamPage's status filter.
  const [archiveFilter, setArchiveFilter] = useState("active");
  const [archiving, setArchiving] = useState(false);
  // Auto-schedule for whichever scope+target is currently chosen in the
  // Generate panel — one schedule per project/member, unlike Digest's single
  // portfolio-wide row.
  const [reportSchedule, setReportSchedule] = useState(null);
  // Edited locally, only sent to the server on "Save changes" — see
  // DigestPage.jsx's identical pattern for why: every field used to autosave
  // on its own, which read as unpredictable for a form with this many
  // fields. `reportSchedule` stays the last value actually persisted.
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  // Every attempt run-scheduled-comms evaluated as due for THIS schedule —
  // distinct from reportSchedule.last_run_at/last_status, which only ever
  // holds the single most recent one.
  const [scheduleHistory, setScheduleHistory] = useState([]);
  // Collapsible sections — all default open, matching how they always
  // rendered before this could be toggled.
  const [scopedUsers, setScopedUsers] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(true);

  const load = async () => {
    const [r, p, u, m, t] = await Promise.all([
      api.get("/reports"),
      api.get("/projects"),
      api.get("/users"),
      api.get("/project-members").catch(() => ({ data: [] })),
      api.get("/team/members").catch(() => null),
    ]);
    setMemberships(m.data || []);
    setReports(r.data);
    setProjects(sortByTitle(p.data));
    setUsers(sortByName(u.data));
    // People in the caller's initiatives — the "Team member" picker. `users` stays
    // the full roster, used only to put names on things (e.g. "Prepared by").
    setScopedUsers(sortByName((t ? t.data : u.data) || []));
    if (highlightId) {
      const hit = r.data.find((x) => x._id === highlightId);
      if (hit) setSelected(hit);
    } else if (r.data[0]) setSelected(r.data[0]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const generate = async () => {
    if (!target) { toast.error("Choose a target"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/reports/generate", { scope, target_id: target, week_start: week === NO_WEEK ? undefined : week });
      toast.success("Report generated");
      setSelected(data);
      const r = await api.get("/reports");
      setReports(r.data);
    } catch (e) {
      // generate-report throws plain Errors (no_api_key / invalid_api_key /
      // forbidden / etc.) with the real message on e.message, not
      // e.response.data.detail — pass the whole error so the real reason shows.
      toast.error(formatApiErrorDetail(e));
    }
    finally { setGenerating(false); }
  };

  // Auto-schedule follows whichever scope+target is picked above — switching
  // the target loads THAT target's own schedule, not the previous one.
  useEffect(() => {
    if (!target) { setReportSchedule(null); return; }
    let cancelled = false;
    api.get(`/reports/schedule?scope_type=${scope}&scope_id=${target}`)
      .then(({ data }) => { if (!cancelled) setReportSchedule(data); })
      .catch(() => { if (!cancelled) setReportSchedule(null); });
    return () => { cancelled = true; };
  }, [scope, target]);

  // Delivery history is keyed by schedule_id, not scope/target — only loads
  // once the schedule has actually been saved at least once.
  const loadScheduleHistory = (scheduleId) => {
    if (!scheduleId) { setScheduleHistory([]); return; }
    api.get(`/reports/schedule/history?schedule_id=${scheduleId}`)
      .then(({ data }) => setScheduleHistory(data || []))
      .catch(() => setScheduleHistory([]));
  };
  useEffect(() => { loadScheduleHistory(reportSchedule?.id); }, [reportSchedule?.id]);

  // The draft always starts equal to whatever was last persisted for the
  // CURRENT target — on first load for that target, and again after every
  // successful save, so "Save changes" only re-enables once something is
  // actually edited.
  useEffect(() => { setScheduleDraft(reportSchedule); }, [reportSchedule]);
  const updateScheduleDraft = (patch) => setScheduleDraft((d) => ({ ...(d || {}), ...patch }));
  const scheduleDirty = JSON.stringify(scheduleDraft) !== JSON.stringify(reportSchedule);

  // Persists the WHOLE draft in one call — the "Save changes" button, not a
  // per-field autosave.
  const saveReportSchedule = async () => {
    if (!target || !scheduleDraft) return;
    setScheduleSaving(true);
    try {
      const { data } = await api.put("/reports/schedule", {
        scope_type: scope,
        scope_id: target,
        enabled: !!scheduleDraft.enabled,
        recipients: scheduleDraft.recipients || [],
        cc: scheduleDraft.cc || [],
        bcc: scheduleDraft.bcc || [],
        day_of_week: scheduleDraft.day_of_week || "sat",
        hour: Number(scheduleDraft.hour ?? 9),
        minute: Number(scheduleDraft.minute ?? 0),
      });
      setReportSchedule(data);
      toast.success("Schedule saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setScheduleSaving(false);
    }
  };

  /*
    Requested directly: same fix as DigestPage.jsx's scope picker — a
    non-admin (manager or member) choosing a project/team member to generate
    a report for should only be offered the ones they're actually part of,
    never the whole org's list. The backend (generate-report) already
    rejects a request for something outside the caller's scope (403), but the
    dropdown itself used to list every project/user regardless, which reads
    as "generate for anyone" until you actually try it. An admin still sees
    everything. "Part of" = owns the project, or holds a project_members row
    on it — the same test project RLS itself uses.
  */
  const myProjectIds = useMemo(() => {
    if (canReportOnAnyone || !currentUser) return null;
    const ids = new Set();
    projects.forEach((p) => { if (p.owner_id === currentUser.id) ids.add(p._id); });
    memberships.forEach((m) => { if (m.user_id === currentUser.id) ids.add(m.project_id); });
    return ids;
  }, [canReportOnAnyone, currentUser, projects, memberships]);
  const myProjects = myProjectIds ? projects.filter((p) => myProjectIds.has(p._id)) : projects;
  const myPeopleIds = useMemo(() => {
    if (!myProjectIds) return null;
    const ids = new Set(currentUser ? [currentUser.id] : []);
    myProjects.forEach((p) => {
      if (p.owner_id) ids.add(p.owner_id);
      memberships.filter((m) => m.project_id === p._id).forEach((m) => ids.add(m.user_id));
    });
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProjectIds, memberships, currentUser]);
  const myUsers = myPeopleIds ? users.filter((u) => myPeopleIds.has(u._id)) : scopedUsers;

  const pMap = useMemo(() => Object.fromEntries(projects.map((p) => [p._id, p.title])), [projects]);
  const uMap = useMemo(() => Object.fromEntries(users.map((u) => [u._id, u.name || u.email])), [users]);
  const targetLabel = useCallback(
    (r) => (r ? (r.scope === "project" ? pMap[r.target_id] : uMap[r.target_id]) || "—" : "—"),
    [pMap, uMap],
  );
  /**
   * The single source of truth for both the preview iframe and the download —
   * whatever you see is byte-for-byte the file you get.
   *
   * Reports written by the HTML prompt arrive as a complete document and are
   * passed straight through; older markdown rows (and any fragment the model
   * returns without the doctype wrapper) get converted and wrapped in the same
   * shell, so history renders in the new design instead of staying plain.
   */
  const reportHtml = useMemo(() => {
    if (!selected) return "";
    const content = selected.content || "";
    if (isFullHtmlDoc(content)) return sanitizeReportDocument(content);

    const label = targetLabel(selected);
    const generatedAt = selected.created_at ? new Date(selected.created_at) : null;
    const bodyHtml = isHtmlContent(content) ? content : markdownToHtml(content);

    return sanitizeReportDocument(
      buildReportDoc({
        title: selected.title || `${label} — Status Report`,
        eyebrow: "NG-IT Initiative Board",
        subtitle: selected.scope === "member" ? "Team member progress summary" : "Project status report",
        meta: [
          { label: "Scope", value: selected.scope === "member" ? "Team member" : "Project" },
          { label: selected.scope === "member" ? "Member" : "Project", value: label },
          // A weekly report records its Monday-to-Saturday window in metadata.
          ...(selected.metadata?.week_start
            ? [
                { label: "Report from", value: fmtReportDay(selected.metadata.week_start) },
                { label: "Report to", value: fmtReportDay(selected.metadata.week_end) },
              ]
            : []),
          {
            label: "Generated",
            value: generatedAt && !Number.isNaN(generatedAt.getTime())
              ? generatedAt.toLocaleString()
              : "—",
          },
          { label: "Prepared by", value: uMap[selected.created_by] || "—" },
        ],
        bodyHtml,
        footer:
          "Source: NG-IT Initiative Board — generated from initiatives, projects, tasks, task activity and comments. " +
          "Figures reflect the board at generation time.",
      }),
    );
  }, [selected, targetLabel, uMap]);

  const download = useCallback(() => {
    if (!reportHtml) return;
    try {
      const blob = new Blob([reportHtml], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = reportFileName(
        targetLabel(selected),
        selected?.created_at ? new Date(selected.created_at) : new Date(),
      );
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  }, [reportHtml, selected, targetLabel]);

  // Sandboxed: the report is model-generated HTML, so it renders with no script
  // execution and no access to the parent page. allow-popups only so the board
  // links inside a report still open.
  const IFRAME_SANDBOX = "allow-popups allow-popups-to-escape-sandbox";

  /*
    The three address pools, derived from the report actually selected.

      To   the owner group + the manager of the project this report is about
      Cc   the owner group + that project's members
      Bcc  everyone (and Bcc also takes a typed address)

    A member-scoped report has no project, so its To adds the member the report
    is about — the person it concerns is the person it is addressed to.
  */
  const owners = useMemo(() => users.filter(isEligibleInitiativeOwner), [users]);

  const mailPools = useMemo(() => {
    const project = selected?.scope === "project"
      ? projects.find((p) => p._id === selected.target_id)
      : null;

    const projectManager = project ? users.filter((u) => u._id === project.owner_id) : [];
    const projectMemberIds = project
      ? new Set(memberships.filter((m) => m.project_id === project._id).map((m) => m.user_id))
      : new Set();
    const projectMembers = users.filter((u) => projectMemberIds.has(u._id));
    const subject = selected?.scope === "member"
      ? users.filter((u) => u._id === selected.target_id)
      : [];

    const dedupe = (list) => {
      const seen = new Set();
      return list.filter((u) => u && u.email && !seen.has(u._id) && seen.add(u._id));
    };

    return {
      to: dedupe([...owners, ...projectManager, ...subject]),
      cc: dedupe([...owners, ...projectManager, ...projectMembers]),
      bcc: dedupe(scopedUsers),
    };
  }, [selected, projects, users, scopedUsers, memberships, owners]);

  const teamEmails = useMemo(() => scopedUsers.map((u) => u.email).filter(Boolean), [scopedUsers]);

  /*
    Two independent filters over the history.

    Choosing a project shows only that project's reports; choosing a member
    shows only that member's. They are ANDed, but a report is about EITHER a
    project or a member, so setting both would always be empty — picking one
    therefore resets the other. That is friendlier than showing an empty list
    and leaving the reader to work out why.
  */
  const filteredReports = useMemo(() => reports.filter((r) => {
    // "Active" and "All" both hide archived reports — only the explicit
    // "Archived" option reveals them, so an archived report is never a
    // surprise sitting in the default/all view.
    if (archiveFilter !== "archived" && r.archived) return false;
    if (archiveFilter === "archived" && !r.archived) return false;
    if (histProject !== "all") return r.scope === "project" && r.target_id === histProject;
    if (histMember !== "all") return r.scope === "member" && r.target_id === histMember;
    return true;
  }), [reports, histProject, histMember, archiveFilter]);

  const toggleArchive = async (report) => {
    if (!report) return;
    const id = report._id || report.id;
    setArchiving(true);
    try {
      if (report.archived) {
        await api.delete(`/reports/${id}/archive`);
        toast.success("Report unarchived");
      } else {
        await api.post(`/reports/${id}/archive`, {});
        toast.success("Report archived");
      }
      const { data: fresh } = await api.get("/reports");
      setReports(fresh);
      const stillThere = fresh.find((r) => (r._id || r.id) === id);
      setSelected(stillThere || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setArchiving(false);
    }
  };

  // Only offer entities that actually have a report, so neither list is a
  // wall of options that all lead nowhere.
  const projectsWithReports = useMemo(() => {
    const ids = new Set(reports.filter((r) => r.scope === "project").map((r) => r.target_id));
    return projects.filter((p) => ids.has(p._id));
  }, [reports, projects]);

  const membersWithReports = useMemo(() => {
    const ids = new Set(reports.filter((r) => r.scope === "member").map((r) => r.target_id));
    return users.filter((u) => ids.has(u._id));
  }, [reports, users]);

  const sendReport = async () => {
    if (!selected) return;
    if ((mail.to || []).length === 0) { toast.error("Add at least one To recipient"); return; }
    const everyone = allRecipients(mail);

    setSending(true);
    try {
      await api.post("/reports/send", {
        report_id: selected._id || selected.id,
        to: mail.to,
        cc: mail.cc,
        bcc: mail.bcc,
        message: sendMessage,
      });
      toast.success(`Sent to ${everyone.length} recipient${everyone.length > 1 ? "s" : ""}: ${everyone.join(", ")}`);
      setMail({ to: [], cc: [], bcc: [] });
      setSendMessage("");
      const { data: fresh } = await api.get("/reports");
      setReports(fresh);
      setSelected(fresh.find((r) => r._id === (selected._id || selected.id)) || selected);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSending(false);
    }
  };

  const actions = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => toggleArchive(selected)}
        disabled={archiving || !selected}
        data-testid="report-archive-button"
        className="rounded-sm"
      >
        {selected?.archived
          ? <><ArrowUUpLeftIcon size={14} className="mr-1.5" /> Unarchive</>
          : <><ArchiveIcon size={14} className="mr-1.5" /> Archive</>}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={download}
        disabled={!reportHtml}
        data-testid="report-download-button"
        className="rounded-sm"
      >
        <DownloadSimpleIcon size={14} className="mr-1.5" /> Download HTML
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setExpanded(true)}
        disabled={!reportHtml}
        data-testid="report-expand-button"
        className="rounded-sm"
      >
        <ArrowsOutSimpleIcon size={14} className="mr-1.5" /> Expand
      </Button>
    </div>
  );

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">AI-generated</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Status Reports</h1>
        </div>
      </div>

      <div className="px-8 py-6 trk-fade-in grid lg:grid-cols-[380px_1fr] gap-6">
        {/* Left: generator + list */}
        <div className="space-y-4">
          <div className="border border-border rounded-sm p-4">
            <div className="trk-uppercase-label mb-3">Generate new</div>
            <Tabs value={scope} onValueChange={(v) => { setScope(v); setTarget(""); }}>
              <TabsList className="rounded-sm grid grid-cols-2">
                <TabsTrigger value="project" data-testid="scope-project">Project</TabsTrigger>
                <TabsTrigger value="member" data-testid="scope-member">Team Member</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="mt-3">
              <Label className="trk-uppercase-label mb-2 block">Target</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="rounded-sm" data-testid="report-target-select"><SelectValue placeholder={scope === "project" ? "Choose project" : "Choose member"} /></SelectTrigger>
                <SelectContent>
                  {scope === "project"
                    ? myProjects.map((p) => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)
                    : myUsers.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Which week the report covers. Weeks run Mon–Sat like the Digest;
                "Overall" keeps the old behaviour (everything, as of now). */}
            <div className="mt-3">
              <Label className="trk-uppercase-label mb-2 block">Period</Label>
              <WeekPicker value={week} onChange={setWeek} />
            </div>
            <Button onClick={generate} disabled={generating || !target} data-testid="generate-report-button" className="w-full mt-3 rounded-sm bg-[#0A0A0A] hover:bg-black text-white">
              <SparkleIcon size={14} className="mr-1" weight="fill" /> {generating ? "Generating..." : "Generate AI Report"}
            </Button>
            <div className="text-[10px] text-zinc-500 mt-2 font-mono">Powered by Claude Sonnet 4.5</div>
          </div>

          {/* Auto-schedule: weekly generate + email, unattended, for whichever
              scope+target is chosen above. One schedule per project/member —
              picking a different target loads that target's own schedule. */}
          {target && (
            <div className="border border-border rounded-sm p-4" data-testid="report-schedule-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="trk-uppercase-label flex items-center gap-1">
                  <ClockCounterClockwiseIcon size={12} /> Auto-schedule
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!scheduleDraft?.enabled}
                    disabled={scheduleSaving}
                    onCheckedChange={(v) => updateScheduleDraft({ enabled: v })}
                    data-testid="report-schedule-enabled-switch"
                  />
                  <button
                    type="button"
                    onClick={() => setScheduleOpen((v) => !v)}
                    data-testid="report-schedule-toggle"
                    className="p-1 rounded-sm hover:bg-zinc-100 text-zinc-500"
                    aria-label={scheduleOpen ? "Collapse" : "Expand"}
                  >
                    <CaretDownIcon size={14} className={`transition-transform ${scheduleOpen ? "" : "-rotate-90"}`} />
                  </button>
                </div>
              </div>
              {scheduleOpen && scheduleDraft && (
              <>
              <p className="text-[10px] text-zinc-500 mb-3 leading-relaxed">
                Weekly, this generates a fresh report for {scope === "project" ? "this project" : "this member"} and
                emails it to the recipients below — no one has to click Generate or Send.
              </p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div>
                  <Label className="trk-uppercase-label mb-1 block">Day</Label>
                  <Select
                    value={scheduleDraft.day_of_week || "sat"}
                    onValueChange={(v) => updateScheduleDraft({ day_of_week: v })}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="report-schedule-day-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_DAYS.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label mb-1 block">Hour (UTC)</Label>
                  <Select
                    value={String(scheduleDraft.hour ?? 9)}
                    onValueChange={(v) => updateScheduleDraft({ hour: Number(v) })}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="report-schedule-hour-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }).map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label mb-1 block">Minute (UTC)</Label>
                  {/* IST (and other half-hour-offset zones) cannot land on a
                      whole UTC hour — this is how "7:00 PM IST" becomes
                      possible to actually select (13:30 UTC). */}
                  <Select
                    value={String(scheduleDraft.minute ?? 0)}
                    onValueChange={(v) => updateScheduleDraft({ minute: Number(v) })}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="report-schedule-minute-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map((m) => <SelectItem key={m} value={String(m)}>:{String(m).padStart(2, "0")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* pg_cron (and run-scheduled-comms) evaluate entirely in UTC —
                  this is the one place that UTC time is translated into
                  whatever timezone the person reading it is actually in, so
                  "why didn't it send at 7 PM" has an answer before saving. */}
              <div className="text-[10px] font-mono text-zinc-500 mb-3 -mt-1">
                Day/hour/minute above are UTC. Next run lands at{" "}
                <span className="font-semibold text-zinc-700">
                  {localPreview(scheduleDraft.day_of_week, scheduleDraft.hour ?? 9, scheduleDraft.minute ?? 0)}
                </span>{" "}
                in your own time zone.
              </div>

              <ScheduleRecipientFields
                key={`${scope}-${target}`}
                value={scheduleDraft}
                onChange={updateScheduleDraft}
                testId="report-schedule"
              />

              <Button
                onClick={saveReportSchedule}
                disabled={!scheduleDirty || scheduleSaving}
                data-testid="report-schedule-save-button"
                className="w-full rounded-sm bg-[#0176D3] hover:bg-[#005FB2] text-white disabled:opacity-50 mb-1"
              >
                {scheduleSaving ? "Saving…" : scheduleDirty ? "Save changes" : "Saved"}
              </Button>

              <div className="text-[10px] font-mono text-zinc-400 leading-tight mt-1">
                {scheduleDirty && <div className="text-amber-600">Unsaved changes.</div>}
                {reportSchedule?.last_run_at && (
                  <>Last: {new Date(reportSchedule.last_run_at).toLocaleString()} — {reportSchedule.last_status}</>
                )}
              </div>

              <div className="mt-3">
                <Label className="trk-uppercase-label mb-1 block">Weekly delivery history</Label>
                <div className="border border-border rounded-sm px-2">
                  <ScheduleDeliveryHistory rows={scheduleHistory} testId="report-schedule-history" />
                </div>
              </div>
              </>
              )}
            </div>
          )}

          <div className="border border-border rounded-sm">
            <div className={historyOpen ? "p-3 border-b border-border" : "p-3"}>
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                data-testid="report-history-toggle"
                className="w-full flex items-center justify-between trk-uppercase-label mb-2"
              >
                <span>History ({filteredReports.length}{filteredReports.length !== reports.length ? ` of ${reports.length}` : ""})</span>
                <CaretDownIcon size={12} className={`transition-transform text-zinc-400 ${historyOpen ? "" : "-rotate-90"}`} />
              </button>
              {historyOpen && (
              <div className="space-y-2">
                <div>
                  <Label className="trk-uppercase-label text-[10px] mb-1 block">Show</Label>
                  <Select value={archiveFilter} onValueChange={setArchiveFilter}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="report-archive-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label text-[10px] mb-1 block">By project</Label>
                  <Select
                    value={histProject}
                    onValueChange={(v) => { setHistProject(v); if (v !== "all") setHistMember("all"); }}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="report-history-project">
                      <SelectValue placeholder="All projects" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All projects</SelectItem>
                      {projectsWithReports.map((p) => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label text-[10px] mb-1 block">By member</Label>
                  <Select
                    value={histMember}
                    onValueChange={(v) => { setHistMember(v); if (v !== "all") setHistProject("all"); }}
                  >
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="report-history-member">
                      <SelectValue placeholder="All members" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All members</SelectItem>
                      {membersWithReports.map((u) => <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {(histProject !== "all" || histMember !== "all") && (
                  <button
                    type="button"
                    onClick={() => { setHistProject("all"); setHistMember("all"); }}
                    data-testid="report-history-clear"
                    className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              )}
            </div>
            {historyOpen && (
            <div className="max-h-[60vh] overflow-y-auto">
              <PagedList items={filteredReports} pageSize={15} testIdPrefix="reports" className="px-3 pb-2">
                {(rows) => (
                  <div className="-mx-3">
              {rows.map((r) => (
                <button
                  key={r._id}
                  onClick={() => setSelected(r)}
                  data-testid={`report-item-${r._id}`}
                  className={`w-full text-left px-3 py-3 border-b border-border last:border-0 hover:bg-zinc-50 ${selected?._id === r._id ? "bg-[#EFF6FF] border-l-2 border-l-[#0176D3]" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                        {r.scope}
                        {r.archived && <ArchiveIcon size={11} className="text-zinc-400" />}
                      </div>
                      <div className="font-medium text-sm truncate">{targetLabel(r)}</div>
                    </div>
                    <div className="text-[10px] text-zinc-400 font-mono">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                </button>
              ))}
                  </div>
                )}
              </PagedList>
              {reports.length === 0 && <div className="p-6 text-sm text-zinc-500">No reports yet.</div>}
            </div>
            )}
          </div>
        </div>

        {/* Right: rendered report */}
        <div className="border border-border rounded-sm min-h-[70vh] flex flex-col">
          {selected ? (
            <>
              <div className="p-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="trk-uppercase-label">{selected.scope} report</div>
                  <h2 className="font-display text-2xl font-extrabold tracking-tight truncate">{targetLabel(selected)}</h2>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">Generated {new Date(selected.created_at).toLocaleString()}</div>
                </div>
                {actions}
              </div>
              <iframe
                title="Report preview"
                sandbox={IFRAME_SANDBOX}
                srcDoc={reportHtml}
                data-testid="report-preview-frame"
                className="w-full flex-1 min-h-[70vh] border-0 bg-[#EEF1F0]"
              />

              {/* Email it, same as a digest. The report travels as an .html
                  attachment rather than in the body: its design lives in a
                  <style> block and most mail clients strip those, so an inline
                  copy would arrive unstyled. */}
              <div className="border-t border-border p-5 bg-zinc-50/40" data-testid="report-send-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="trk-uppercase-label">Send email</div>
                  {selected.sent_at && (
                    <div className="text-[10px] font-mono text-emerald-700 flex items-center gap-1">
                      <CheckCircleIcon size={12} weight="fill" /> Sent · {new Date(selected.sent_at).toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Who it has already gone to, off reports.metadata.sent_to —
                    the chips below are cleared on success, so without this the
                    page would keep no visible record. */}
                {(selected.sent_to || []).length > 0 && (
                  <div className="mb-3 border border-emerald-200 bg-emerald-50 rounded-sm px-3 py-2" data-testid="report-sent-to">
                    <div className="trk-uppercase-label text-emerald-800 mb-1">
                      Delivered to {(selected.sent_to || []).length}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(selected.sent_to || []).map((r) => (
                        <span key={r} className="text-[11px] font-mono bg-white border border-emerald-200 rounded-sm px-1.5 py-0.5 text-emerald-900">{r}</span>
                      ))}
                    </div>
                  </div>
                )}

                <RecipientFields
                  value={mail}
                  onChange={setMail}
                  pools={mailPools}
                  testId="report-recipients"
                />

                <div className="mt-3">
                  <Label className="trk-uppercase-label mb-1 block">Message (optional)</Label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    The email always opens with this fixed greeting — your note is added as one more
                    paragraph right after it, before the report body:
                    <br />
                    <span className="italic">
                      "Dear All, Pls refer the below status update for{" "}
                      {targetLabel(selected) !== "—"
                        ? targetLabel(selected)
                        : selected?.scope === "member" ? "Team member" : "Project"}
                      ."
                    </span>
                  </p>
                  <Textarea
                    value={sendMessage}
                    onChange={(e) => setSendMessage(e.target.value)}
                    placeholder="Add a note to appear above the report in the email…"
                    rows={3}
                    className="rounded-sm"
                    data-testid="report-send-message"
                  />
                </div>

                <Button
                  onClick={sendReport}
                  disabled={sending || !reportHtml || (mail.to || []).length === 0}
                  data-testid="report-send-button"
                  className="w-full mt-3 rounded-sm bg-[#0176D3] hover:bg-[#005FB2] text-white disabled:opacity-50"
                >
                  <PaperPlaneRightIcon size={14} className="mr-1" weight="fill" />
                  {sending ? "Sending…" : `Send to ${allRecipients(mail).length || 0}`}
                </Button>
                <div className="text-[10px] font-mono text-zinc-500 mt-2 leading-tight">
                  Goes out from the organisation&apos;s Microsoft 365 mailbox, with the report laid out in the
                  email body — same as a digest.
                </div>
              </div>
            </>
          ) : (
            <div className="p-10 text-sm text-zinc-500">Generate a report to preview it here.</div>
          )}
        </div>
      </div>

      {/* Expanded report view */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          data-testid="report-expanded-dialog"
          className="max-w-none w-[96vw] h-[94vh] p-0 gap-0 rounded-sm flex flex-col sm:rounded-sm"
        >
          {/* pr-14 leaves room for DialogContent's own absolute close X at
              right-4, which would otherwise sit on top of Collapse. */}
          <div className="pl-5 pr-14 py-4 border-b border-border flex items-start justify-between gap-4 flex-wrap shrink-0">
            <div className="min-w-0">
              <div className="trk-uppercase-label">{selected?.scope} report — expanded</div>
              <DialogTitle className="font-display text-xl font-extrabold tracking-tight truncate">
                {selected?.title || targetLabel(selected)}
              </DialogTitle>
              {selected?.created_at && (
                <div className="text-xs text-zinc-500 mt-1 font-mono">
                  Generated {new Date(selected.created_at).toLocaleString()}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleArchive(selected)}
                disabled={archiving || !selected}
                data-testid="report-archive-button-expanded"
                className="rounded-sm"
              >
                {selected?.archived
                  ? <><ArrowUUpLeftIcon size={14} className="mr-1.5" /> Unarchive</>
                  : <><ArchiveIcon size={14} className="mr-1.5" /> Archive</>}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={download}
                disabled={!reportHtml}
                data-testid="report-download-button-expanded"
                className="rounded-sm"
              >
                <DownloadSimpleIcon size={14} className="mr-1.5" /> Download HTML
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpanded(false)}
                data-testid="report-collapse-button"
                className="rounded-sm"
              >
                <ArrowsInSimpleIcon size={14} className="mr-1.5" /> Collapse
              </Button>
            </div>
          </div>
          <iframe
            title="Report expanded"
            sandbox={IFRAME_SANDBOX}
            srcDoc={widenReportDoc(reportHtml)}
            data-testid="report-expanded-frame"
            className="w-full flex-1 border-0 bg-[#EEF1F0]"
          />
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
